import { State, toDataSourceRequestString } from '@progress/kendo-data-query';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridColumnMenuFilter,
  GridDataStateChangeEvent,
  GridNoRecords
} from '@progress/kendo-react-grid';
import { useRef, useState } from 'react';
import { ColumnMenu } from '../components/columnMenu';
import YesNoDialog from '../components/dialogs/yes-no-dialog';
import { ActionCell } from '../components/grid/actions-cell';
import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../components/grid/grid-tool-bar';
import Toolbar from '../components/page-toolbar';
import { DefaultGridSettings } from '../constants/grid-settings';
import {
  getGlobalDocumentLatestAsync,
  patchFeedbackAsAdminAsync
} from '../services/feedback';
import { appStore } from '../stores/app-store';
import { CustomFilterLanguage } from './filter/custom-filter-language';
import { CardViewDataStateChangeEvent } from './organisations-cardview';
import { NoRecords } from '../components/grid/no-records';
import { YesNoDialogResult } from '../types/dialog-result';
import { FeedbackApproval } from '../types/feedback';
import { FeedbackManagementCardView } from './feedbacks-management-cardview';
import { formatDateToDDMMYYYY } from '../utils/date';
import { useToast } from '../contexts/toast-context';
import { useGetFeedbacks } from '../hooks/services/use-feedbacks';
import { Loader } from '@progress/kendo-react-indicators';

const initialDataState: State = {
  sort: [{ field: 'name', dir: 'asc' }],
  ...DefaultGridSettings.initialDataState
};

const FeedbacksManagementGrid = () => {
  const [dataState, setDataState] = useState<State>(initialDataState);
  const [gridMode, setGridMode] = useState<string>('grid');
  const [selectedId, setSelectedId] = useState<number>(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);

  const { onToastOpen } = useToast();

  const { data, isLoading, mutate } = useGetFeedbacks(
    toDataSourceRequestString(dataState),
    'library'
  );

  const onActionRow = (props: GridCellProps) => {
    return (
      <>
        <ActionCell
          title={'Delete feedback'}
          icon={'delete'}
          themeColor={'error'}
          className={'fs-2'}
          onClick={() => onDelete(props.dataItem.id)}
        />
      </>
    );
  };

  const onDelete = (id: number) => {
    onDeleteFeedback(id);
  };

  const onDeleteFeedback = async (id: number) => {
    setSelectedId(id);
    setShowDeleteConfirm(true);
  };

  const unpublishFeedback = async (feedbackId: number) => {
    // Get latest document
    let latestDocument = await getGlobalDocumentLatestAsync(feedbackId);

    // Patch the document status
    let request: FeedbackApproval = {
      fileName: latestDocument?.fileName ?? '',
      version: latestDocument?.version ?? '',
      status: 'New',
      isGlobal: false,
      isDeleted: false
    };
    await patchFeedbackAsAdminAsync(feedbackId!, latestDocument.id, request);
  };

  const onConfirmDelete = async (result: YesNoDialogResult) => {
    if (result === 'yes') {
      try {
        appStore.showLoading();

        await unpublishFeedback(selectedId);
        mutate();

        onToastOpen({
          title: 'Global library feedbacks',
          body: <div>Feedback deleted.</div>,
          style: 'success'
        });
      } catch {
        onToastOpen({
          title: 'Global library feedbacks',
          body: <div>Failed to delete feedback.</div>,
          style: 'error'
        });
      } finally {
        appStore.hideLoading();
      }
    }
    setShowDeleteConfirm(false);
  };

  const closeDeleteDialog = () => {
    setShowDeleteConfirm(false);
  };

  const gridExportRef = useRef<ExcelExport | null>(null);

  const DateCell = (props: GridCellProps) => {
    return (
      <td className={'k-table-td'}>
        {formatDateToDDMMYYYY(props.dataItem.createdDateUtc, true)}
      </td>
    );
  };

  return (
    <>
      <Toolbar title={'Global Feedback'}></Toolbar>
      <div
        className={'pt-2'}
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden'
        }}>
        <GridToolBar
          searchPlaceholder={'Search feedback'}
          columnsToSearch={['name']}
          exportRef={gridExportRef}
          onGridModeChange={(e) => {
            setGridMode(e.gridMode);
          }}
          {...dataState}
          onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}></GridToolBar>
        <ExcelExport
          data={data?.data}
          ref={gridExportRef}
          fileName={`feedback.xlsx`}>
          {gridMode === 'grid' && (
            <Grid
              id={'grid-feedback'}
              style={{
                flexGrow: 1,
                minHeight: 0,
                height: '100%',
                overflow: 'hidden'
              }}
              pageable={DefaultGridSettings.pagerSettings}
              sortable={true}
              data={data}
              {...dataState}
              onDataStateChange={(e: GridDataStateChangeEvent) => {
                setDataState(e.dataState);
              }}>
              <Column field={'name'} title={'Name'} columnMenu={ColumnMenu} />
              <Column
                field={'createdDateUtc'}
                title={'Created'}
                cells={{ data: DateCell }}
              />
              <Column
                field={'version'}
                title={'Version'}
                columnMenu={ColumnMenu}
              />
              <Column
                field={'language'}
                title={'Languages'}
                columnMenu={(props: any) => (
                  <GridColumnMenuFilter
                    {...props}
                    filterUI={CustomFilterLanguage}
                  />
                )}
              />
              <Column
                field={'organisation'}
                title={'Organisation Name'}
                columnMenu={ColumnMenu}
              />

              <Column
                title={'Actions'}
                cells={{ data: onActionRow }}
                width={120}
              />

              <GridNoRecords>
                {isLoading ? (
                  <div
                    className={
                      'd-flex flex-column align-items-center gap-2 text-muted'
                    }>
                    <Loader type={'pulsing'} />
                    <span>Loading data</span>
                  </div>
                ) : (
                  <NoRecords />
                )}
              </GridNoRecords>
            </Grid>
          )}

          {gridMode === 'card' && (
            <FeedbackManagementCardView
              data={data?.data ?? []}
              total={data?.total}
              onDelete={onDelete}
              {...dataState}
              onDataStateChange={(e: CardViewDataStateChangeEvent) => {
                setDataState(e.dataState);
              }}
            />
          )}
        </ExcelExport>

        {showDeleteConfirm && (
          <YesNoDialog
            title={'Remove feedback'}
            onConfirm={onConfirmDelete}
            onClose={closeDeleteDialog}>
            Are you sure you want to delete this feedback from Global Library?
          </YesNoDialog>
        )}
      </div>
    </>
  );
};
export default FeedbacksManagementGrid;
